import React, { useState, useEffect } from "react";
import CreatableSelect from 'react-select/creatable';
import api from "../utils/api";

const OfferForm = ({ editingOffer, importedData, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    titulo: "",
    descricao: "",
    preco_original: "",
    preco_desconto: "",
    imagem_url: "",
    loja: "",
    link_afiliado: "",
    categoria: "Eletrônicos",
    subcategoria: "",
    status: "ATIVA",
    link_monitoramento: "",
    mostrar_parcelamento: true,
    parcelas: 10,
    cupom: ""
  });
  const [loading, setLoading] = useState(false);
  const [stores, setStores] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);

  // Fetch stores and categories on mount
  useEffect(() => {
    const fetchStores = async () => {
      try {
        const response = await api.get('/lojas');
        setStores(response.data);
      } catch (error) {
        console.error("Erro ao buscar lojas:", error);
      }
    };

    const fetchCategories = async () => {
      try {
        const response = await api.get('/categorias');
        setCategories(response.data);
      } catch (error) {
        console.error("Erro ao buscar categorias:", error);
      }
    };

    fetchStores();
    fetchCategories();
  }, []);

  // Populate form when editing or importing
  useEffect(() => {
    if (editingOffer) {
      setFormData(editingOffer);
    } else if (importedData) {
      setFormData(prev => ({
        ...prev,
        ...importedData,
        preco_original: importedData.preco_original || "",
        preco_desconto: importedData.preco_desconto || ""
      }));
      // Try to detect store from imported URL
      if (importedData.link_afiliado || importedData.url_original) {
        detectStore(importedData.link_afiliado || importedData.url_original);
      }
    }
  }, [editingOffer, importedData]);

  const detectStore = (url) => {
    if (!url || !stores.length) return;
    const lowerUrl = url.toLowerCase();
    const foundStore = stores.find(store => {
      if (!store.domainPattern) return false;
      const patterns = store.domainPattern.split('|');
      return patterns.some(pattern => lowerUrl.includes(pattern.trim().toLowerCase()));
    });

    if (foundStore) {
      setFormData(prev => ({ ...prev, loja: foundStore.nome }));
    }
  };

  // Filter subcategories when category changes
  useEffect(() => {
    if (formData.categoria) {
      const cat = categories.find(c =>
        c.name === formData.categoria
      );
      setSubcategories(cat?.subcategories || []);
    } else {
      setSubcategories([]);
    }
  }, [formData.categoria, categories]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Auto-detect store when link changes
    if (name === 'link_afiliado') {
      detectStore(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const payload = {
      ...formData,
      preco_original: parseFloat(formData.preco_original),
      preco_desconto: parseFloat(formData.preco_desconto)
    };

    try {
      if (editingOffer && editingOffer.id) {
        // Update existing offer
        await api.put(`/ofertas/${editingOffer.id}`, payload);
        alert("Oferta atualizada com sucesso!");
      } else {
        // Create new offer
        await api.post("/ofertas", payload);
        alert("Oferta criada com sucesso!");
      }

      // Reset form
      setFormData({
        titulo: "",
        descricao: "",
        preco_original: "",
        preco_desconto: "",
        imagem_url: "",
        loja: "",
        link_afiliado: "",
        categoria: "Eletrônicos",
        subcategoria: "",
        status: "ATIVA",
        link_monitoramento: "",
        mostrar_parcelamento: true,
        parcelas: 10,
        cupom: ""
      });

      if (onSuccess) onSuccess();
    } catch (error) {
      console.error("Erro ao salvar oferta:", error);
      alert("Erro ao salvar oferta. Verifique os dados e tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-group">
        <label>Título *</label>
        <input
          type="text"
          name="titulo"
          value={formData.titulo}
          onChange={handleChange}
          required
          placeholder="Nome do produto"
        />
      </div>

      <div className="form-group">
        <label>Descrição</label>
        <textarea
          name="descricao"
          value={formData.descricao}
          onChange={handleChange}
          placeholder="Descrição detalhada do produto"
          rows="4"
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        <div className="form-group">
          <label>Preço Original (R$)</label>
          <input
            type="number"
            name="preco_original"
            value={formData.preco_original}
            onChange={handleChange}
            step="0.01"
            placeholder="0.00"
          />
        </div>

        <div className="form-group">
          <label>Preço com Desconto (R$) *</label>
          <input
            type="number"
            name="preco_desconto"
            value={formData.preco_desconto}
            onChange={handleChange}
            step="0.01"
            required
            placeholder="0.00"
          />
        </div>
      </div>

      <div className="form-group">
        <label>Cupom de Desconto</label>
        <input
          type="text"
          name="cupom"
          value={formData.cupom}
          onChange={handleChange}
          placeholder="Ex: PROMO10"
        />
        <small>Opcional. Será exibido com destaque na oferta.</small>
      </div>

      <div className="form-group">
        <label>URL da Imagem *</label>
        <input
          type="url"
          name="imagem_url"
          value={formData.imagem_url}
          onChange={handleChange}
          required
          placeholder="https://exemplo.com/imagem.jpg"
        />
        {formData.imagem_url && (
          <div style={{ marginTop: '10px' }}>
            <img
              src={formData.imagem_url}
              alt="Preview"
              style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'contain', border: '1px solid #ddd', borderRadius: '5px' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        <div className="form-group">
          <label>Loja *</label>
          <select name="loja" value={formData.loja} onChange={handleChange} required>
            <option value="">Selecione...</option>
            {stores.map(store => (
              <option key={store.id} value={store.nome}>{store.nome}</option>
            ))}
            <option value="Outra">Outra</option>
          </select>
        </div>

        <div className="form-group">
          <label>Categoria *</label>
          <CreatableSelect
            options={categories.map(c => ({ value: c.name, label: c.name }))}
            value={formData.categoria ? { value: formData.categoria, label: formData.categoria } : null}
            onChange={(option) => setFormData(prev => ({
              ...prev,
              categoria: option?.value || "",
              subcategoria: "" // Reset subcategory when category changes
            }))}
            placeholder="Selecione ou digite uma categoria..."
            isClearable
            formatCreateLabel={(inputValue) => `Criar "${inputValue}"`}
            noOptionsMessage={() => "Digite para criar uma nova categoria"}
            styles={{
              control: (base) => ({
                ...base,
                minHeight: '42px',
                borderColor: '#ddd',
                '&:hover': { borderColor: '#3498db' }
              })
            }}
          />
          <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>✨ Selecione ou digite um nome novo para criar automaticamente</small>
        </div>
      </div>

      <div className="form-group">
        <label>Subcategoria</label>
        <CreatableSelect
          options={subcategories.map(sc => ({ value: sc.name, label: sc.name }))}
          value={formData.subcategoria ? { value: formData.subcategoria, label: formData.subcategoria } : null}
          onChange={(option) => setFormData(prev => ({
            ...prev,
            subcategoria: option?.value || ""
          }))}
          placeholder={formData.categoria ? "Selecione ou digite uma subcategoria..." : "Selecione uma categoria primeiro"}
          isClearable
          isDisabled={!formData.categoria}
          formatCreateLabel={(inputValue) => `Criar "${inputValue}"`}
          noOptionsMessage={() => formData.categoria ? "Digite para criar uma nova subcategoria" : "Selecione uma categoria primeiro"}
          styles={{
            control: (base, state) => ({
              ...base,
              minHeight: '42px',
              borderColor: '#ddd',
              backgroundColor: state.isDisabled ? '#f5f5f5' : 'white',
              '&:hover': { borderColor: state.isDisabled ? '#ddd' : '#3498db' }
            })
          }}
        />
        <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>Opcional. A subcategoria será vinculada à categoria acima.</small>
      </div>

      <div className="form-group">
        <label>Link de Afiliado *</label>
        <input
          type="url"
          name="link_afiliado"
          value={formData.link_afiliado}
          onChange={handleChange}
          required
          placeholder="https://..."
        />
        <small>Este é o link que será compartilhado com os usuários</small>
      </div>
      <div className="form-group">
        <label>Link de Monitoramento (Preço) 🔍</label>
        <input
          type="url"
          name="link_monitoramento"
          value={formData.link_monitoramento || ""}
          onChange={handleChange}
          placeholder="https://www.mercadolivre.com.br/p/MLB123..."
        />
        <small style={{ color: '#00a650' }}>
          <strong>Recomendado:</strong> Use o link direto do produto (sem ser de afiliado) para uma leitura de preço 100% estável.
        </small>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        <div className="form-group">
          <label>
            <input
              type="checkbox"
              name="mostrar_parcelamento"
              checked={formData.mostrar_parcelamento}
              onChange={(e) => setFormData(prev => ({ ...prev, mostrar_parcelamento: e.target.checked }))}
              style={{ marginRight: '8px' }}
            />
            Mostrar parcelamento
          </label>
          <small>Exibir opção de parcelamento sem juros</small>
        </div>

        {formData.mostrar_parcelamento && (
          <div className="form-group">
            <label>Número de Parcelas</label>
            <input
              type="number"
              name="parcelas"
              value={formData.parcelas}
              onChange={handleChange}
              min="1"
              placeholder="10"
            />
          </div>
        )}
      </div>

      <div className="form-group">
        <label>Status</label>
        <select name="status" value={formData.status} onChange={handleChange}>
          <option value="ATIVA">Ativa</option>
          <option value="INATIVA">Inativa</option>
          <option value="EXPIRADA">Expirada</option>
        </select>
      </div>

      <div className="form-actions">
        <button type="button" className="btn-secondary" onClick={onCancel} disabled={loading}>
          Cancelar
        </button>
        <button type="submit" className="btn-primary" disabled={loading}>
          {loading ? "Salvando..." : editingOffer?.id ? "Atualizar Oferta" : "Criar Oferta"}
        </button>
      </div>
    </form>
  );
};

export default OfferForm;
